import { Heightfield } from '../heightfield'
import type { Rng } from '../../core/rng'
import { DEG, clamp } from '../../core/math'

/**
 * Erosion. Spec §5.2: "hydraulic-style erosion produces couloirs and gullies
 * naturally [...] erosion passes matter more here because natural drainage is
 * what makes a face readable."
 *
 * Three passes, in order:
 *   1. hydraulicErode  — droplets carve dendritic drainage
 *   2. thermalErode    — material slumps past the angle of repose
 *   3. enforceDrainage — every cell provably drains to the apron
 *
 * The third is not cosmetic. Droplet erosion routinely leaves closed basins,
 * and a closed basin on a ski face is a hole a player falls into and cannot
 * leave — the exact failure the hand-built face shipped with twice. Guaranteeing
 * drainage structurally is much cheaper than finding them one at a time.
 */

// ---------------------------------------------------------------------------
// Hydraulic
// ---------------------------------------------------------------------------

const EROSION_RADIUS = 3
const INERTIA = 0.055
const CAPACITY = 3.4
const MIN_SLOPE = 0.01
const ERODE_SPEED = 0.28
const DEPOSIT_SPEED = 0.24
const EVAPORATE = 0.017
const GRAVITY = 5
const MAX_LIFETIME = 58

interface Brush {
  offsets: Int32Array
  weights: Float32Array
}

/** Weighted disc, so erosion never digs a single-cell spike. */
function makeBrush(cols: number, radius: number): Brush {
  const offs: number[] = []
  const wts: number[] = []
  let total = 0
  for (let dz = -radius; dz <= radius; dz++) {
    for (let dx = -radius; dx <= radius; dx++) {
      const d2 = dx * dx + dz * dz
      if (d2 > radius * radius) continue
      const w = 1 - Math.sqrt(d2) / radius
      offs.push(dz * cols + dx)
      wts.push(w)
      total += w
    }
  }
  const weights = new Float32Array(wts.length)
  for (let i = 0; i < wts.length; i++) weights[i] = wts[i] / total
  return { offsets: new Int32Array(offs), weights }
}

export function hydraulicErode(hf: Heightfield, rng: Rng, droplets: number): void {
  const { cols, rows, height } = hf
  const brush = makeBrush(cols, EROSION_RADIUS)
  const margin = EROSION_RADIUS + 2

  for (let d = 0; d < droplets; d++) {
    // Start high on the face: droplets released in the apron do nothing useful.
    let px = margin + rng() * (cols - 1 - margin * 2)
    let pz = margin + rng() * (rows - 1 - margin * 2) * 0.85
    let dirX = 0
    let dirZ = 0
    let speed = 1
    let water = 1
    let sediment = 0

    for (let life = 0; life < MAX_LIFETIME; life++) {
      const nx = px | 0
      const nz = pz | 0
      const cellOffsetX = px - nx
      const cellOffsetZ = pz - nz
      const i = nz * cols + nx

      // Bilinear height and gradient.
      const h00 = height[i]
      const h10 = height[i + 1]
      const h01 = height[i + cols]
      const h11 = height[i + cols + 1]
      const gradX = (h10 - h00) * (1 - cellOffsetZ) + (h11 - h01) * cellOffsetZ
      const gradZ = (h01 - h00) * (1 - cellOffsetX) + (h11 - h10) * cellOffsetX
      const h =
        h00 * (1 - cellOffsetX) * (1 - cellOffsetZ) +
        h10 * cellOffsetX * (1 - cellOffsetZ) +
        h01 * (1 - cellOffsetX) * cellOffsetZ +
        h11 * cellOffsetX * cellOffsetZ

      dirX = dirX * INERTIA - gradX * (1 - INERTIA)
      dirZ = dirZ * INERTIA - gradZ * (1 - INERTIA)
      const len = Math.hypot(dirX, dirZ)
      if (len < 1e-6) break
      dirX /= len
      dirZ /= len

      px += dirX
      pz += dirZ
      if (px < margin || px >= cols - 1 - margin || pz < margin || pz >= rows - 1 - margin) break

      const newH = sampleGrid(height, cols, px, pz)
      const deltaH = newH - h

      const capacity = Math.max(-deltaH * speed * water * CAPACITY, MIN_SLOPE)

      if (sediment > capacity || deltaH > 0) {
        // Uphill or over capacity: drop material, filling the dip behind us.
        const amount = deltaH > 0 ? Math.min(deltaH, sediment) : (sediment - capacity) * DEPOSIT_SPEED
        sediment -= amount
        height[i] += amount * (1 - cellOffsetX) * (1 - cellOffsetZ)
        height[i + 1] += amount * cellOffsetX * (1 - cellOffsetZ)
        height[i + cols] += amount * (1 - cellOffsetX) * cellOffsetZ
        height[i + cols + 1] += amount * cellOffsetX * cellOffsetZ
      } else {
        const amount = Math.min((capacity - sediment) * ERODE_SPEED, -deltaH)
        for (let b = 0; b < brush.offsets.length; b++) {
          const bi = i + brush.offsets[b]
          if (bi < 0 || bi >= height.length) continue
          const take = amount * brush.weights[b]
          height[bi] -= take
        }
        sediment += amount
      }

      speed = Math.sqrt(Math.max(0, speed * speed - deltaH * GRAVITY))
      water *= 1 - EVAPORATE
      if (water < 0.01) break
    }
  }
}

function sampleGrid(field: Float32Array, cols: number, px: number, pz: number): number {
  const ix = px | 0
  const iz = pz | 0
  const fx = px - ix
  const fz = pz - iz
  const i = iz * cols + ix
  const a = field[i] + (field[i + 1] - field[i]) * fx
  const b = field[i + cols] + (field[i + cols + 1] - field[i + cols]) * fx
  return a + (b - a) * fz
}

// ---------------------------------------------------------------------------
// Thermal
// ---------------------------------------------------------------------------

/**
 * Material slumps until no slope exceeds the angle of repose.
 *
 * This is the pass that keeps the face skiable. Noise plus hydraulic erosion
 * happily produces 60-70° faces everywhere; snow does not sit on those, and
 * neither does a player. Capping at repose gives the slope histogram its shape
 * for free instead of hand-tuning noise amplitudes until it behaves.
 */
export function thermalErode(hf: Heightfield, reposeDeg: number, iterations: number): void {
  const { cols, rows, height, cell } = hf
  const tanRepose = Math.tan(reposeDeg * DEG)
  const talus = 0.5

  // All eight neighbours, each with its own distance. Checking only the four
  // cardinals lets diagonal slopes sit a factor of sqrt(2) steeper than the
  // angle of repose — a 44° cap that actually permits 54°, which is most of a
  // face's steep tail arriving by the back door.
  const offsets = [-1, 1, -cols, cols, -cols - 1, -cols + 1, cols - 1, cols + 1]
  const maxDelta = offsets.map((_, k) => tanRepose * cell * (k < 4 ? 1 : Math.SQRT2))

  for (let it = 0; it < iterations; it++) {
    let moved = 0
    for (let iz = 1; iz < rows - 1; iz++) {
      for (let ix = 1; ix < cols - 1; ix++) {
        const i = iz * cols + ix
        const h = height[i]
        let excess = 0
        let target = -1
        for (let k = 0; k < 8; k++) {
          const diff = h - height[i + offsets[k]] - maxDelta[k]
          if (diff > excess) { excess = diff; target = i + offsets[k] }
        }
        if (target >= 0) {
          const move = excess * talus * 0.5
          height[i] -= move
          height[target] += move
          moved++
        }
      }
    }
    if (moved === 0) break
  }
}

// ---------------------------------------------------------------------------
// Drainage
// ---------------------------------------------------------------------------

/**
 * Priority flood. Guarantees every cell has a monotonically descending path to
 * the apron, by raising closed basins just enough to drain.
 *
 * `grade` is the gradient it leaves behind, in metres per metre — *not* per
 * cell, which is what it used to be, and which meant the drainage gradient
 * silently changed the day the grid went from 2.5m cells to 1.8m.
 *
 * The value has to clear a physical bar. Snow resistance is linear drag rather
 * than Coulomb friction, so a filled basin has no holding angle, just a
 * terminal velocity: `v = g sin(theta) / resistance`. On powder that is
 * 11.3 sin(theta) / 0.205, so a stopped racer needs about 1° to creep at
 * 1 m/s and 3° to move at 3 m/s. The old 0.012m per cell worked out at 0.38°,
 * which drains water and strands people — the audit found six zero-input
 * release points that flooded out onto a filled pond and stayed there.
 *
 * 5.4°, not the 3° that bare terminal velocity implies. A skier stands on the
 * *interpolated* surface, and four cell corners that each satisfy the rule can
 * still meet in a saddle with no slope at all under the skis. At 3° one release
 * balanced on such a saddle at exactly 0.00 m/s for the last six seconds of a
 * 220-second run, while a release 5cm to either side coasted out cleanly.
 *
 * Note the flood gives this for free and needs no second pass: every cell it
 * pops is assigned at least `grade` above the neighbour it drains to, and every
 * cell that keeps its own height already stood higher than that. A separate
 * pit-filling pass run afterwards only manages to dam its own uphill neighbours
 * and create fresh basins — which is exactly what happened when this was tried.
 *
 * 5.4° still bleeds a run to nothing next to a 40° pitch, so a trap is still a
 * trap: you lose all your speed in there and have to fight your way out. It is
 * just never a hole with no exit. Spec §4: no hard fail, you always finish.
 */
export function enforceDrainage(hf: Heightfield, grade = 0.095): number {
  const epsilon = grade * hf.cell
  const { cols, rows, height } = hf
  const n = cols * rows
  const filled = new Float32Array(n)
  const closed = new Uint8Array(n)
  filled.fill(Infinity)

  const heap = new MinHeap(n)

  // The outlet is the downhill edge — the apron. Everything must reach it.
  // Side and top edges seed at their own height too, so the rim doesn't get
  // treated as a dam holding the whole face back.
  const seed = (i: number): void => {
    filled[i] = height[i]
    closed[i] = 1
    heap.push(height[i], i)
  }
  for (let ix = 0; ix < cols; ix++) {
    seed((rows - 1) * cols + ix)
    seed(ix)
  }
  for (let iz = 1; iz < rows - 1; iz++) {
    seed(iz * cols)
    seed(iz * cols + cols - 1)
  }

  let raisedCells = 0
  while (heap.size > 0) {
    const i = heap.pop()
    const hi = filled[i]
    const ix = i % cols
    const iz = (i / cols) | 0

    for (let k = 0; k < 4; k++) {
      const jx = ix + (k === 0 ? -1 : k === 1 ? 1 : 0)
      const jz = iz + (k === 2 ? -1 : k === 3 ? 1 : 0)
      if (jx < 0 || jx >= cols || jz < 0 || jz >= rows) continue
      const j = jz * cols + jx
      if (closed[j]) continue
      const target = Math.max(height[j], hi + epsilon)
      if (target > height[j] + 1e-6) raisedCells++
      filled[j] = target
      closed[j] = 1
      heap.push(target, j)
    }
  }

  for (let i = 0; i < n; i++) if (filled[i] > height[i]) height[i] = filled[i]
  return raisedCells
}

/** Flat binary min-heap over (key, value) pairs. */
class MinHeap {
  private keys: Float64Array
  private vals: Int32Array
  size = 0

  constructor(capacity: number) {
    this.keys = new Float64Array(capacity + 1)
    this.vals = new Int32Array(capacity + 1)
  }

  private grow(): void {
    const k = new Float64Array(this.keys.length * 2)
    k.set(this.keys)
    this.keys = k
    const v = new Int32Array(this.vals.length * 2)
    v.set(this.vals)
    this.vals = v
  }

  push(key: number, val: number): void {
    if (this.size + 1 >= this.keys.length) this.grow()
    let i = this.size++
    this.keys[i] = key
    this.vals[i] = val
    while (i > 0) {
      const p = (i - 1) >> 1
      if (this.keys[p] <= this.keys[i]) break
      this.swap(p, i)
      i = p
    }
  }

  pop(): number {
    const top = this.vals[0]
    this.size--
    if (this.size > 0) {
      this.keys[0] = this.keys[this.size]
      this.vals[0] = this.vals[this.size]
      let i = 0
      for (;;) {
        const l = i * 2 + 1
        const r = l + 1
        let m = i
        if (l < this.size && this.keys[l] < this.keys[m]) m = l
        if (r < this.size && this.keys[r] < this.keys[m]) m = r
        if (m === i) break
        this.swap(m, i)
        i = m
      }
    }
    return top
  }

  private swap(a: number, b: number): void {
    const k = this.keys[a]; this.keys[a] = this.keys[b]; this.keys[b] = k
    const v = this.vals[a]; this.vals[a] = this.vals[b]; this.vals[b] = v
  }
}

/**
 * Audit: how many cells sit in a local minimum with no descending neighbour.
 * Should be zero after enforceDrainage — cheap enough to assert every build.
 */
export function countSinks(hf: Heightfield): number {
  const { cols, rows, height } = hf
  let sinks = 0
  for (let iz = 1; iz < rows - 1; iz++) {
    for (let ix = 1; ix < cols - 1; ix++) {
      const i = iz * cols + ix
      const h = height[i]
      if (
        h <= height[i - 1] && h <= height[i + 1] &&
        h <= height[i - cols] && h <= height[i + cols]
      ) sinks++
    }
  }
  return sinks
}

/** Light smoothing pass, used to take the edge off thermal-erosion stair-stepping. */
export function smooth(hf: Heightfield, strength: number): void {
  const { cols, rows, height } = hf
  const src = Float32Array.from(height)
  for (let iz = 1; iz < rows - 1; iz++) {
    for (let ix = 1; ix < cols - 1; ix++) {
      const i = iz * cols + ix
      const avg = (src[i - 1] + src[i + 1] + src[i - cols] + src[i + cols]) * 0.25
      height[i] = src[i] + (avg - src[i]) * clamp(strength, 0, 1)
    }
  }
}
